import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../../services/api";
import Loading from "../../components/Loading";
import InlineAlert from "../../components/InlineAlert";
import CustomSelect, { SelectOptionType } from "../../pages/UserDocuments/components/CustomSelect";
import { ConfirmDialog } from "../../pages/UserDocuments/components/ConfirmDialog";
import { useHistory } from "react-router-dom";
import GuaranteeDescription from "../../components/GuaranteeDescription";

import {
  PageShell,
  PageContainer,
  ModalContainer,
  ModalHeader,
  ModalBody,
  TopBar,
  UploadButton,
  HiddenFileInput,
  HelperText,
  FormGrid,
  Field,
  Input,
  PrimaryButton,
  ActionsRow,
  FilesList,
  FileItem,
  FileInfo,
  EmptyState,
  FileActions,
  PreviewContainer,
} from "./styles";

import { FiUpload, FiSend, FiEye, FiDownload, FiTrash2 } from "react-icons/fi";

type Guarantee = {
  id: number;
  name: string;
  description: string;
  documents: string | string[];
};

type PublicLinkResponse = {
  property_id: number;
  rent_process_id: number;
  property?: {
    id: number;
    title?: string;
    address?: any;
    photo?: string;
  } | null;
  guarantees: Guarantee[];
  expires_at?: string | null;
};

type PublicFile = {
  id: number;
  original_name: string;
  mime_type: string;
  created_at?: string;
};

const CIVIL_STATE_OPTIONS = [
  "Solteiro(a)",
  "Casado(a)",
  "Separado(a)",
  "Divorciado(a)",
  "Viúvo(a)",
];

function normalizeDocuments(docs: string | string[] | null | undefined): string[] {
  if (!docs) return [];
  if (Array.isArray(docs)) return docs;

  if (typeof docs === "string") {
    try {
      const parsed = JSON.parse(docs);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  return [];
}

function formatAddress(address: any): string {
  if (!address) return "";
  if (typeof address === "string") return address;

  // Public flows must not expose number/complement/floor.
  const line = String(address.address ?? "").trim();
  const city = `${address.neighborhood ? `${address.neighborhood} - ` : ""}${address.city ?? ""}${
    address.uf ? `/${address.uf}` : ""
  }`;

  return [line, city].filter(Boolean).join(" • ").replace(/\s+/g, " ").trim();
}

export default function PublicSendDocumentsPage() {
  const search = useMemo(() => new URLSearchParams(window.location.search), []);
  const token = search.get("token") ?? "";
  const history = useHistory();

  const [loading, setLoading] = useState(true);
  const [uiError, setUiError] = useState("");
  const [uiSuccess, setUiSuccess] = useState("");

  const [linkData, setLinkData] = useState<PublicLinkResponse | null>(null);

  const [guarantees, setGuarantees] = useState<Guarantee[]>([]);
  const [selectedGuaranteeId, setSelectedGuaranteeId] = useState<number | "">("");

  const [civilState, setCivilState] = useState("");
  const [profession, setProfession] = useState("");

  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);

  const [files, setFiles] = useState<PublicFile[]>([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [previewFile, setPreviewFile] = useState<(PublicFile & { url: string }) | null>(null);

  const [showConfirm, setShowConfirm] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<PublicFile | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const guaranteeOptions: SelectOptionType[] = useMemo(
    () => guarantees.map((g) => ({ value: String(g.id), label: g.name })),
    [guarantees]
  );

  const civilStateOptions: SelectOptionType[] = useMemo(
    () => CIVIL_STATE_OPTIONS.map((s) => ({ value: s, label: s })),
    []
  );

  const guaranteeObject = useMemo(() => {
    if (!selectedGuaranteeId) return undefined;
    return guarantees.find((g) => g.id === selectedGuaranteeId);
  }, [guarantees, selectedGuaranteeId]);

  const addressLabel = useMemo(() => {
    return formatAddress(linkData?.property?.address);
  }, [linkData]);

  const baseFilesPath = useMemo(() => {
    if (!token) return "";
    return `/api/client/document-link/${token}/files`;
  }, [token]);

  async function loadFiles() {
    if (!baseFilesPath) return;

    setFilesLoading(true);
    try {
      const res = await api.get(baseFilesPath);
      setFiles(res.data?.data ?? []);
    } catch (err) {
      console.error(err);
      setFiles([]);
    } finally {
      setFilesLoading(false);
    }
  }

    useEffect(() => {
    async function loadLink() {
        if (!token) {
        history.replace("/login", {
            from: "/schedule-process",
        });
        return;
        }

        try {
        setUiError("");
        setUiSuccess("");
        setLoading(true);

        const res = await api.get(`/api/client/document-link/${token}`);
        const data: PublicLinkResponse = res.data?.data;

        setLinkData(data);
        setGuarantees(data?.guarantees ?? []);

        if (data?.guarantees?.length) {
            setSelectedGuaranteeId(data.guarantees[0].id);
        } else {
            setSelectedGuaranteeId("");
        }

        await loadFiles();
        } catch (e) {
        history.replace("/login", {
            from: "/schedule-process",
        });
        } finally {
        setLoading(false);
        }
    }

    loadLink();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

  function openFilePicker() {
    fileInputRef.current?.click();
  }

  async function handlePickFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = e.target.files;
    if (!picked || picked.length === 0) return;

    try {
      setUiError("");
      setUiSuccess("");
      setUploading(true);

      const form = new FormData();
      form.append("token", token);

      Array.from(picked).forEach((file) => {
        form.append("documents[]", file);
      });

      await api.post("/api/client/documents", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setUiSuccess("Arquivos enviados com sucesso!");
      await loadFiles();
    } catch (err) {
      console.error(err);
      setUiError("Erro ao enviar documentos. Tente novamente mais tarde.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }

  async function getFileUrl(fileId: number, download = false) {
    const res = await api.get(`${baseFilesPath}/${fileId}`, {
      params: download ? { download: 1 } : {},
    });

    return (res.data?.data?.url ?? res.data?.url) as string;
  }

  async function handlePreview(file: PublicFile) {
    try {
      setUiError("");
      const url = await getFileUrl(file.id, false);
      setPreviewFile({ ...file, url });
    } catch (err) {
      console.error(err);
      setUiError("Erro ao carregar pré-visualização.");
    }
  }

  async function handleDownload(file: PublicFile) {
    try {
      setUiError("");
      const url = await getFileUrl(file.id, true);

      const link = document.createElement("a");
      link.href = url;
      link.download = file.original_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error(err);
      setUiError("Erro ao baixar documento.");
    }
  }

  function askDelete(file: PublicFile) {
    setFileToDelete(file);
    setShowConfirm(true);
  }

  async function confirmDelete() {
    if (!fileToDelete) return;

    try {
      setUiError("");
      setDeleting(true);

      await api.delete(`${baseFilesPath}/${fileToDelete.id}`);

      setFiles((prev) => prev.filter((f) => f.id !== fileToDelete.id));
      if (previewFile?.id === fileToDelete.id) setPreviewFile(null);

      setUiSuccess("Arquivo excluído com sucesso!");
      setShowConfirm(false);
      setFileToDelete(null);
    } catch (err) {
      console.error(err);
      setUiError("Erro ao excluir documento.");
    } finally {
      setDeleting(false);
    }
  }

  function renderPreview() {
    if (!previewFile) return null;

    if (previewFile.mime_type === "application/pdf") {
      return <iframe src={previewFile.url} title="PDF Preview" />;
    }

    if (previewFile.mime_type.startsWith("image/")) {
      return <img src={previewFile.url} alt={previewFile.original_name} />;
    }

    return <p>Pré-visualização indisponível</p>;
  }

  async function handleSend() {
    setUiError("");
    setUiSuccess("");

    if (!civilState) return setUiError("Estado civil é obrigatório.");
    if (!profession.trim()) return setUiError("Ocupação é obrigatória.");
    if (!selectedGuaranteeId) return setUiError("Garantia é obrigatória.");
    if (files.length === 0) return setUiError("Envie ao menos um documento.");

    try {
      setSending(true);

      const res = await api.post("/api/client/documents/send", {
        token,
        material_status: civilState,
        profession,
        guarantee_id: selectedGuaranteeId,
        express_offer: 0,
      });

      if (res.data?.success) {
        setUiSuccess("Documentação enviada para análise com sucesso!");
      } else {
        setUiError("Não foi possível enviar para análise.");
      }
    } catch (err) {
      console.error(err);
      setUiError("Erro ao enviar documentação para análise.");
    } finally {
      setSending(false);
    }
  }

  return (
    <PageShell>
      <PageContainer>
        <ModalContainer>
          <ConfirmDialog
            open={showConfirm}
            title="Excluir documento"
            description={
              fileToDelete
                ? `Deseja excluir o arquivo "${fileToDelete.original_name}"? Essa ação não poderá ser desfeita.`
                : ""
            }
            loading={deleting}
            onCancel={() => {
              setShowConfirm(false);
              setFileToDelete(null);
            }}
            onConfirm={confirmDelete}
          />

          <ModalHeader>
            <div>
              <h3>Envio de documentação</h3>
              {linkData?.property?.title ? <small>{linkData.property.title}</small> : null}
              {addressLabel ? <small>{addressLabel}</small> : null}
            </div>
          </ModalHeader>

          <ModalBody>
            {loading && <Loading />}

            {!loading && (
              <>
                <TopBar>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <UploadButton type="button" onClick={openFilePicker} disabled={uploading}>
                      <FiUpload />
                      {uploading ? "Enviando..." : "Adicionar documentos"}
                    </UploadButton>

                    <HiddenFileInput
                      ref={fileInputRef}
                      type="file"
                      multiple
                      onChange={handlePickFiles}
                      accept="application/pdf,image/*"
                    />
                  </div>

                  <HelperText>
                    Envie PDF ou imagens (JPG/PNG). Depois, preencha os dados e clique em “Enviar para análise”.
                  </HelperText>

                  {uiError && (
                    <InlineAlert type="error" onClose={() => setUiError("")}>
                      {uiError}
                    </InlineAlert>
                  )}
                  {uiSuccess && (
                    <InlineAlert type="success" onClose={() => setUiSuccess("")}>
                      {uiSuccess}
                    </InlineAlert>
                  )}

                  <FormGrid>
                    <Field>
                      <label>Garantia</label>
                      <CustomSelect
                        value={selectedGuaranteeId ? String(selectedGuaranteeId) : ""}
                        options={guaranteeOptions}
                        placeholder="Selecione"
                        onChange={(val) => setSelectedGuaranteeId(val ? Number(val) : "")}
                      />
                    </Field>

                    <Field>
                      <label>Estado civil</label>
                      <CustomSelect
                        value={civilState}
                        options={civilStateOptions}
                        placeholder="Selecione"
                        onChange={(val) => setCivilState(val)}
                      />
                    </Field>

                    <Field style={{ gridColumn: "1 / -1" }}>
                      <label>Ocupação</label>
                      <Input
                        value={profession}
                        onChange={(e) => setProfession(e.target.value)}
                        placeholder="Ex: Desenvolvedor"
                      />
                    </Field>

                    {guaranteeObject ? (
                      <Field style={{ gridColumn: "1 / -1" }}>
                        <label>Descrição da garantia</label>
                        <div
                          style={{
                            background: "#f9fafb",
                            border: "1px solid #e5e7eb",
                            borderRadius: 10,
                            padding: 12,
                            marginBottom: 10,
                          }}
                        >
                          <GuaranteeDescription
                            description={guaranteeObject.description}
                          />
                        </div>

                        <label>Documentos exigidos</label>
                        <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.35 }}>
                          {normalizeDocuments(guaranteeObject.documents).length === 0 ? (
                            <div>• Nenhum documento específico informado</div>
                          ) : (
                            normalizeDocuments(guaranteeObject.documents).map((d) => (
                              <div key={d}>• {d}</div>
                            ))
                          )}
                        </div>
                      </Field>
                    ) : null}
                  </FormGrid>

                  <ActionsRow>
                    <PrimaryButton type="button" onClick={handleSend} disabled={sending}>
                      <FiSend />
                      {sending ? "Enviando..." : "Enviar para análise"}
                    </PrimaryButton>
                  </ActionsRow>
                </TopBar>

                {filesLoading ? (
                  <Loading />
                ) : files.length === 0 ? (
                  <EmptyState>
                    <p>Nenhum documento enviado ainda.</p>
                  </EmptyState>
                ) : (
                  <>
                    <FilesList>
                      {files.map((file) => (
                        <FileItem key={file.id}>
                          <FileInfo>
                            <strong>{file.original_name}</strong>
                            <span>{file.mime_type}</span>
                          </FileInfo>

                          <FileActions>
                            <FiEye title="Visualizar" onClick={() => handlePreview(file)} />
                            <FiDownload title="Download" onClick={() => handleDownload(file)} />
                            <FiTrash2 title="Excluir" onClick={() => askDelete(file)} />
                          </FileActions>
                        </FileItem>
                      ))}
                    </FilesList>

                    {previewFile && <PreviewContainer>{renderPreview()}</PreviewContainer>}
                  </>
                )}
              </>
            )}
          </ModalBody>
        </ModalContainer>
      </PageContainer>
    </PageShell>
  );
}
